import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, Mail, Lock, Eye, EyeOff, ArrowRight, KeyRound, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  // Forgot password modal state
  const [fpOpen, setFpOpen] = useState(false);
  const [fpStep, setFpStep] = useState(1); // 1: email, 2: code, 3: new password
  const [fpEmail, setFpEmail] = useState('');
  const [fpCode, setFpCode] = useState('');
  const [fpResetToken, setFpResetToken] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpNewPasswordConfirm, setFpNewPasswordConfirm] = useState('');
  const [fpLoading, setFpLoading] = useState(false);

  const translateLoginError = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail === 'Account pending admin approval') {
      return 'Hesabınız henüz admin tarafından onaylanmadı.';
    }
    if (detail === 'Invalid email or password') {
      return 'E-posta veya şifre hatalı.';
    }
    if (detail === 'Token expired') {
      return 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.';
    }
    if (detail === 'Invalid token' || detail === 'Please login again') {
      return 'Oturum doğrulaması başarısız. Lütfen tekrar giriş yapın.';
    }
    return detail || 'Giriş başarısız';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Giriş başarılı!');
    } catch (error) {
      toast.error(translateLoginError(error));
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setFpStep(1);
    setFpEmail('');
    setFpCode('');
    setFpResetToken('');
    setFpNewPassword('');
    setFpNewPasswordConfirm('');
    setFpOpen(true);
  };

  const handleFpSendCode = async (e) => {
    e.preventDefault();
    setFpLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/forgot-password`, { email: fpEmail });
      toast.success('Kod e-posta adresinize gönderildi. (Gelen kutusunda göremezseniz lütfen Spam kutunuzu kontrol ediniz)');
      setFpStep(2);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bir hata oluştu, tekrar deneyin');
    } finally {
      setFpLoading(false);
    }
  };

  const handleFpVerifyCode = async (e) => {
    e.preventDefault();
    setFpLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/verify-reset-code`, { email: fpEmail, code: fpCode });
      setFpResetToken(res.data.reset_token);
      setFpStep(3);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Kod hatalı veya süresi dolmuş');
    } finally {
      setFpLoading(false);
    }
  };

  const handleFpResetPassword = async (e) => {
    e.preventDefault();
    if (fpNewPassword !== fpNewPasswordConfirm) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    if (fpNewPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    setFpLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { token: fpResetToken, new_password: fpNewPassword });
      toast.success('Şifreniz başarıyla güncellendi');
      setFpOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bir hata oluştu, tekrar deneyin');
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80"
          alt="Stock market analysis"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2E2620]/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#C86F4A] flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">
              BIST Analiz
            </h1>
          </div>
          <p className="text-white/80 text-lg max-w-md">
            Biz insanların yaptığı hataları yapmıyoruz; sadece matematiği takip ediyoruz.
            <br />
            <span className="italic text-white/70">JIM SIMONS.</span>
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#F6F1EA] p-8">
        <div className="w-full max-w-md animate-slide-up">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-full bg-[#C86F4A] flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#2E2620] font-['Playfair_Display']">
              BIST Analiz
            </h1>
          </div>

          <Card className="border-[#E6DCCF] shadow-[0_4px_24px_rgba(46,38,32,0.08)]">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-['Playfair_Display'] text-[#2E2620]">
                Hoş Geldiniz
              </CardTitle>
              <CardDescription className="text-[#7A6A5C]">
                Hesabınıza giriş yapın
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2E2620]">E-posta</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89F91]" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 border-[#E6DCCF] focus:border-[#C86F4A] focus:ring-[#C86F4A]/20"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2E2620]">Şifre</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89F91]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 border-[#E6DCCF] focus:border-[#C86F4A] focus:ring-[#C86F4A]/20"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A89F91] hover:text-[#2E2620] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={openForgotPassword}
                      className="text-sm text-[#C86F4A] hover:text-[#B05D3A] transition-colors"
                    >
                      Şifremi Unuttum
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full py-6 font-medium transition-all"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Giriş yapılıyor...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Giriş Yap
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-[#7A6A5C]">
                  Hesabınız yok mu?{' '}
                  <Link
                    to="/register"
                    className="text-[#C86F4A] hover:text-[#B05D3A] font-medium transition-colors"
                    data-testid="register-link"
                  >
                    Kayıt Olun
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={fpOpen} onOpenChange={setFpOpen}>
        <DialogContent className="sm:max-w-md bg-[#F6F1EA] border-[#E6DCCF]">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-[#2E2620] flex items-center gap-2">
              {fpStep === 1 && <><KeyRound className="w-5 h-5 text-[#C86F4A]" /> Şifremi Unuttum</>}
              {fpStep === 2 && <><ShieldCheck className="w-5 h-5 text-[#C86F4A]" /> Doğrulama Kodu</>}
              {fpStep === 3 && <><Lock className="w-5 h-5 text-[#C86F4A]" /> Yeni Şifre</>}
            </DialogTitle>
            <DialogDescription className="text-[#7A6A5C]">
              {fpStep === 1 && 'Kayıtlı e-posta adresinizi girin, size sıfırlama kodu göndereceğiz.'}
              {fpStep === 2 && `${fpEmail} adresine gönderilen 6 haneli kodu girin.`}
              {fpStep === 3 && 'Yeni şifrenizi belirleyin.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Email */}
          {fpStep === 1 && (
            <form onSubmit={handleFpSendCode} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="fp-email" className="text-[#2E2620]">E-posta</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89F91]" />
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    className="pl-10 border-[#E6DCCF] focus:border-[#C86F4A] focus:ring-[#C86F4A]/20"
                    required
                    autoFocus
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={fpLoading}
                className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
              >
                {fpLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Gönderiliyor...
                  </span>
                ) : 'Kod Gönder'}
              </Button>
            </form>
          )}

          {/* Step 2: Code */}
          {fpStep === 2 && (
            <form onSubmit={handleFpVerifyCode} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="fp-code" className="text-[#2E2620]">Doğrulama Kodu</Label>
                <Input
                  id="fp-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  value={fpCode}
                  onChange={(e) => setFpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.4em] border-[#E6DCCF] focus:border-[#C86F4A] focus:ring-[#C86F4A]/20 font-mono"
                  required
                  autoFocus
                  maxLength={6}
                />
                <p className="text-xs text-[#A89F91] text-center">Kod 15 dakika geçerlidir</p>
              </div>
              <Button
                type="submit"
                disabled={fpLoading || fpCode.length !== 6}
                className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
              >
                {fpLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Doğrulanıyor...
                  </span>
                ) : 'Kodu Doğrula'}
              </Button>
              <button
                type="button"
                onClick={() => setFpStep(1)}
                className="w-full text-sm text-[#7A6A5C] hover:text-[#2E2620] transition-colors"
              >
                ← E-postayı değiştir
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {fpStep === 3 && (
            <form onSubmit={handleFpResetPassword} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="fp-newpass" className="text-[#2E2620]">Yeni Şifre</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89F91]" />
                  <Input
                    id="fp-newpass"
                    type="password"
                    placeholder="En az 6 karakter"
                    value={fpNewPassword}
                    onChange={(e) => setFpNewPassword(e.target.value)}
                    className="pl-10 border-[#E6DCCF] focus:border-[#C86F4A] focus:ring-[#C86F4A]/20"
                    required
                    autoFocus
                    minLength={6}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fp-newpass-confirm" className="text-[#2E2620]">Şifre Tekrar</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A89F91]" />
                  <Input
                    id="fp-newpass-confirm"
                    type="password"
                    placeholder="••••••••"
                    value={fpNewPasswordConfirm}
                    onChange={(e) => setFpNewPasswordConfirm(e.target.value)}
                    className="pl-10 border-[#E6DCCF] focus:border-[#C86F4A] focus:ring-[#C86F4A]/20"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={fpLoading}
                className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
              >
                {fpLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Kaydediliyor...
                  </span>
                ) : 'Şifremi Güncelle'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
